// Documents (CRM 2.0 M3-F12; rows M3.49, M3.50; DC-171 / DC-172 / DC-173).
// Side menu "Documents" and card ⋮ Documents. Unit picker when the customer has several CRM 2.0 units.
// Groups: Your KYC · From VJ · Requests. Only the current version of each document; View / Download ask the
// server for a 10-minute link; "Download all" makes one zip of the current documents (consent point download_docs).
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCrm2Cards } from "../../Services/Crm2Booking";
import { listCrm2Documents, zipCrm2Documents, crm2DocLinkById } from "../../Services/Crm2Portal";
import { errorTost } from "../../Services/Toast";
import { dismissLoader, presentLoader } from "../../Services/loader";
import useConsentPoint from "../../Components/Home/Crm2/TermsGate";
import { niceDate } from "../../Components/Home/Crm2/Crm2Parts";
import css from "../../Components/Home/Crm2/Crm2Booking.module.css";

function Group({ title, empty, items, apId, onOpen }) {
	return (
		<div className={css.sl}>
			<div className={css.slH}>
				<b>{title}</b>
				<span>{items.length ? `${items.length} document${items.length > 1 ? "s" : ""}` : ""}</span>
			</div>
			{!items.length && (
				<div className={css.sr}>
					<div className={css.srM}>
						<div className={css.srS}>{empty}</div>
					</div>
				</div>
			)}
			{items.map((d) => (
				<div key={d.documentId} className={`${css.sr} ${css.ok}`}>
					<i>✓</i>
					<div className={css.srM}>
						<div className={css.srT}>
							{d.applicantNo ? `Applicant ${d.applicantNo} · ` : ""}
							{d.label}
						</div>
						<div className={css.srS}>
							Latest{d.version > 1 ? ` · version ${d.version}` : ""}
							{d.at ? ` · ${niceDate(d.at)}` : ""}
							{d.note ? ` · ${d.note}` : ""}
						</div>
					</div>
					{d.hasFile && (
						<div className={css.srA}>
							<span className={css.lk} onClick={() => onOpen(apId, d, false)}>
								View
							</span>{" "}
							·{" "}
							<span className={css.lk} onClick={() => onOpen(apId, d, true)}>
								Download
							</span>
						</div>
					)}
				</div>
			))}
		</div>
	);
}

export default function Documents() {
	const location = useLocation();
	const wanted = new URLSearchParams(location.search).get("ap");
	const [cards, setCards] = useState(null);
	const [apId, setApId] = useState(wanted || "");
	const [docs, setDocs] = useState(null);
	const { requirePoint, termsModal } = useConsentPoint();

	useEffect(() => {
		getCrm2Cards()
			.then((list) => {
				const mine = (list || []).filter((c) => c?.isCrm2);
				setCards(mine);
				if (!wanted && mine.length) setApId(mine[0].allotmentPaymentId);
			})
			.catch((err) => errorTost(err.message));
	}, [wanted]);

	useEffect(() => {
		if (!apId) return;
		setDocs(null);
		listCrm2Documents(apId)
			.then(setDocs)
			.catch((err) => errorTost(err.message));
	}, [apId]);

	const card = useMemo(() => (cards || []).find((c) => c.allotmentPaymentId === apId), [cards, apId]);

	const open = (id, d, download) =>
		requirePoint("download_docs", id, () => {
			crm2DocLinkById(id, d.documentId)
				.then((l) => {
					const a = document.createElement("a");
					a.href = l.url;
					a.target = "_blank";
					a.rel = "noreferrer";
					if (download) a.download = l.name || d.label;
					a.click();
				})
				.catch((err) => errorTost(err.message));
		});

	const downloadAll = () =>
		requirePoint("download_docs", apId, () => {
			presentLoader();
			zipCrm2Documents(apId)
				.then((z) => {
					dismissLoader();
					const url = URL.createObjectURL(z.blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = z.fileName;
					a.click();
					setTimeout(() => URL.revokeObjectURL(url), 60000);
				})
				.catch((err) => {
					dismissLoader();
					errorTost(err.message);
				});
		});

	return (
		<div style={{ padding: "1.6rem", display: "flex", flexDirection: "column", gap: "1.6rem", fontFamily: "Inter-Regular" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1.2rem", flexWrap: "wrap" }}>
				<div>
					<div className={css.unitName} style={{ fontSize: "2rem" }}>
						Documents
					</div>
					<div className={css.sub2}>Your current documents for each unit. Older versions stay with VJ.</div>
				</div>
				<div style={{ display: "flex", gap: "1.2rem", alignItems: "center", flexWrap: "wrap" }}>
					{cards && cards.length > 1 && (
						<select value={apId} onChange={(e) => setApId(e.target.value)} className={css.btn}>
							{cards.map((c) => (
								<option key={c.allotmentPaymentId} value={c.allotmentPaymentId}>
									{c.unit?.label} · {(c.unit?.projectName || "").split(" ").slice(0, 2).join(" ")}
								</option>
							))}
						</select>
					)}
					<button type="button" className={`${css.btn} ${css.btnP}`} disabled={!docs || !(docs.yourKyc.length + docs.fromVj.length)} onClick={downloadAll}>
						Download all
					</button>
				</div>
			</div>
			{cards && !cards.length && <p className={css.muted}>No documents yet.</p>}
			{card && (
				<p className={css.muted}>
					{card.unit?.label} · {card.unit?.projectName}
				</p>
			)}
			{docs && (
				<>
					<Group title="Your KYC" empty="Your Agreement KYC files show here after you submit it." items={docs.yourKyc} apId={apId} onOpen={open} />
					<Group title="From VJ" empty="Documents from VJ show here as your booking moves on." items={docs.fromVj} apId={apId} onOpen={open} />
					{docs.requests.length > 0 && <Group title="Requests" empty="" items={docs.requests} apId={apId} onOpen={open} />}
				</>
			)}
			{termsModal}
		</div>
	);
}
